import { useEffect, useState, type ReactNode } from "react";

import { PlanType } from "@optidev/shared-types";
import { Check, Loader2 } from "lucide-react";
import toast from "react-hot-toast";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { billingApi, PricingResponse } from "@/lib/api/billing";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useCurrencyContext } from "@/hooks/useCurrencyContext";
import { useEmbedStore } from "@/stores/embed-store";

import { CurrencySelect } from "@/routes/dashboard/billing/CurrencySelect";
import {
  PLAN_INFO,
  CURRENCY_SYMBOLS,
  getPlanPrices,
} from "@/routes/dashboard/billing/plan-info";

interface PublishUpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The viewer's current plan — FREE when this modal is shown. */
  currentPlanType: PlanType;
  projectName?: string;
  /** Override the default publish-specific header (e.g. for the out-of-credits context). */
  title?: string;
  description?: string;
}

// Plans offered as starting subscriptions (Enterprise routes to a sales demo, not checkout).
const OFFERED_PLANS: PlanType[] = [
  PlanType.STANDARD,
  PlanType.PRO,
  PlanType.ENTERPRISE,
];

export function PublishUpsellModal({
  isOpen,
  onClose,
  projectName,
  title,
  description,
}: PublishUpsellModalProps): JSX.Element {
  const { getIdToken } = useAuth();
  const { isAdminOrOwner } = usePermissions();
  const { context: currencyContext } = useCurrencyContext();

  const [pricing, setPricing] = useState<PricingResponse | null>(null);
  const [isAnnual, setIsAnnual] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);

  const selectedCurrency = currencyContext.displayCurrency || "usd";
  const symbol = CURRENCY_SYMBOLS[selectedCurrency] || "$";
  const hasMultipleCurrencies =
    (currencyContext.availableCurrencies?.length ?? 0) > 1;

  // Public pricing — fetch lazily the first time the modal opens.
  useEffect(() => {
    if (!isOpen || pricing) return;
    let cancelled = false;
    void billingApi
      .getPricing()
      .then((data) => {
        if (!cancelled) setPricing(data);
      })
      .catch((err) => {
        console.error("Failed to load pricing:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, pricing]);

  const handleUpgrade = async (planType: PlanType): Promise<void> => {
    if (planType === PlanType.ENTERPRISE) {
      window.open(
        "https://calendly.com/optidev/demo/",
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }

    try {
      setCheckoutLoading(planType);
      const token = await getIdToken();
      if (!token) {
        toast.error("Authentication required");
        setCheckoutLoading(null);
        return;
      }

      const { url } = await billingApi.createCheckoutSession(
        token,
        planType as PlanType.STANDARD | PlanType.PRO,
        isAnnual ? "year" : "month",
        selectedCurrency,
      );

      // Stripe can't load inside an iframe — embed users get a new tab.
      if (useEmbedStore.getState().isEmbedded) {
        window.open(url, "_blank");
        toast.success("Checkout opened in new tab");
        setCheckoutLoading(null);
      } else {
        window.location.href = url;
      }
    } catch (err: unknown) {
      console.error("Failed to start checkout:", err);
      const message =
        err instanceof Error ? err.message : "Failed to start checkout";
      toast.error(message);
      setCheckoutLoading(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-[95vw] w-full sm:max-w-4xl lg:max-w-6xl rounded-3xl p-0 gap-0 overflow-hidden"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="max-h-[92vh] overflow-y-auto">
          {/* Header */}
          <div className="flex flex-col gap-6 px-8 pt-9 pb-7 sm:px-10 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-2 max-w-xl">
              <h2 className="text-2xl font-semibold tracking-tight">
                {title ?? "Choose a plan to publish"}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {description ??
                  (projectName
                    ? `Subscribe to a plan to publish “${projectName}” — unlock private apps, custom domains, and more.`
                    : "Subscribe to a plan to publish your project — unlock private apps, custom domains, and more.")}
              </p>
              {!isAdminOrOwner && (
                <p className="text-sm font-medium text-muted-foreground">
                  Ask a workspace admin to upgrade.
                </p>
              )}
            </div>

            <div className="flex flex-shrink-0 items-center gap-3">
              {/* Monthly / Annual segmented toggle */}
              <div className="inline-flex items-center rounded-full bg-muted p-1">
                <button
                  type="button"
                  onClick={() => setIsAnnual(false)}
                  className={cn(
                    "rounded-full px-4 py-1.5 text-sm font-medium whitespace-nowrap transition-colors duration-150",
                    !isAnnual
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setIsAnnual(true)}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium whitespace-nowrap transition-colors duration-150",
                    isAnnual
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  Annual
                  <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                    Save 10%
                  </span>
                </button>
              </div>

              {hasMultipleCurrencies && <CurrencySelect />}
            </div>
          </div>

          {/* Plans */}
          <div className="px-8 pb-8 sm:px-10 lg:pt-4">
            <div className="grid grid-cols-1 items-stretch gap-5 md:grid-cols-3">
              {OFFERED_PLANS.map((planType) => (
                <PlanColumn
                  key={planType}
                  planType={planType}
                  pricing={pricing}
                  currency={selectedCurrency}
                  symbol={symbol}
                  isAnnual={isAnnual}
                  highlighted={planType === PlanType.PRO}
                  loading={checkoutLoading === planType}
                  disabled={!isAdminOrOwner}
                  onUpgrade={() => handleUpgrade(planType)}
                />
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface PlanColumnProps {
  planType: PlanType;
  pricing: PricingResponse | null;
  currency: string;
  symbol: string;
  isAnnual: boolean;
  highlighted: boolean;
  loading: boolean;
  disabled: boolean;
  onUpgrade: () => void;
}

function PlanColumn({
  planType,
  pricing,
  currency,
  symbol,
  isAnnual,
  highlighted,
  loading,
  disabled,
  onUpgrade,
}: PlanColumnProps): JSX.Element {
  const info = PLAN_INFO[planType];
  const { monthlyPrice, yearlyPrice } = getPlanPrices(
    pricing,
    planType,
    currency,
  );

  const isCustomPrice = typeof monthlyPrice !== "number";
  const monthlyNum = typeof monthlyPrice === "number" ? monthlyPrice : 0;
  const yearlyNum = typeof yearlyPrice === "number" ? yearlyPrice : 0;
  const perMonth = isAnnual ? yearlyNum / 12 : monthlyNum;
  // Only strike the monthly price when annual genuinely costs less per month.
  const showSavings = isAnnual && !isCustomPrice && yearlyNum / 12 < monthlyNum;

  const isEnterprise = planType === PlanType.ENTERPRISE;

  return (
    <div
      className={cn(
        "relative flex flex-col rounded-2xl border bg-card p-6 transition-shadow duration-150",
        highlighted
          ? // The recommended plan is emphasised but stays aligned with its neighbours.
            "border-primary/40 shadow-lg ring-1 ring-primary/20"
          : "border-border shadow-sm hover:shadow-md",
      )}
    >
      {highlighted && (
        <span className="absolute -top-3 left-6 rounded-full bg-primary px-3 py-1 text-[11px] font-semibold text-primary-foreground shadow-sm">
          Most popular
        </span>
      )}

      {/* Title */}
      <div className="space-y-1">
        <h3 className="text-lg font-semibold">{info.name}</h3>
        <p className="text-sm text-muted-foreground">{info.description}</p>
      </div>

      {/* Price */}
      <div className="mt-6 min-h-[68px]">
        {isCustomPrice ? (
          <div className="text-3xl font-bold tracking-tight">Custom</div>
        ) : (
          <>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold tracking-tight">
                {symbol}
                {perMonth.toFixed(2)}
              </span>
              {showSavings && (
                <span className="text-lg text-muted-foreground line-through">
                  {symbol}
                  {monthlyNum.toFixed(2)}
                </span>
              )}
              <span className="text-sm text-muted-foreground">/month</span>
            </div>
            {isAnnual && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                {symbol}
                {yearlyNum.toFixed(2)}/year billed annually
              </p>
            )}
          </>
        )}
      </div>

      {/* CTA */}
      <Button
        size="lg"
        onClick={onUpgrade}
        disabled={disabled || loading}
        className={cn(
          "mt-6 w-full rounded-xl",
          isEnterprise
            ? "bg-foreground text-background hover:bg-foreground/90"
            : "bg-primary text-primary-foreground hover:bg-primary/90",
        )}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Redirecting...
          </>
        ) : (
          info.ctaText || `Get ${info.name}`
        )}
      </Button>

      {/* Features */}
      <div className="mt-7 space-y-3 border-t border-border/60 pt-6">
        {info.featureHeader && (
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {info.featureHeader}
          </p>
        )}
        {info.monthlyCredits && (
          <Feature>
            {info.monthlyCredits} credits per month
            {isAnnual && (
              <span className="text-muted-foreground"> · refills monthly</span>
            )}
          </Feature>
        )}
        {info.features.map((feature) => (
          <Feature key={feature}>{feature}</Feature>
        ))}
      </div>
    </div>
  );
}

function Feature({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="flex items-start gap-2.5">
      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <span className="text-sm leading-relaxed text-foreground/90">
        {children}
      </span>
    </div>
  );
}
